import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, Clock, LayoutGrid, ArrowUp, ArrowDown } from "lucide-react";

const EMPTY = { title_html: "", subtitle: "" };

export default function HeroCasesEditor() {
  const [rotation, setRotation] = useState([]);
  const [seconds, setSeconds] = useState(6);
  const [casesPerRow, setCasesPerRow] = useState(2);
  const [classicLabel, setClassicLabel] = useState("");
  const [clientsPageSize, setClientsPageSize] = useState(18);
  const [clientsRotateSec, setClientsRotateSec] = useState(8);
  const [clientsLogoScale, setClientsLogoScale] = useState(72);
  const [heroSubHome, setHeroSubHome] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/content/kv/home_sections").then(({ data }) => {
      if (data) {
        setHeroSubHome(data);
        setRotation(Array.isArray(data.hero_rotation) ? data.hero_rotation : []);
        setSeconds(Number(data.hero_rotation_seconds) || 6);
        setCasesPerRow(Number(data.cases_per_row) || 2);
        setClassicLabel(data.classic_label || "");
        setClientsPageSize(Number(data.clients_page_size) || 18);
        setClientsRotateSec(Number(data.clients_rotate_sec) || 8);
        setClientsLogoScale(Number(data.clients_logo_scale) || 72);
      }
    });
  }, []);

  const add = () => {
    setRotation([...rotation, { ...EMPTY }]);
  };
  const remove = (i) => setRotation(rotation.filter((_, j) => j !== i));
  const move = (i, dir) => setRotation((arr) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    const a = [...arr];
    [a[i], a[j]] = [a[j], a[i]];
    return a;
  });
  const upd = (i, k, v) => setRotation((arr) => { const a = [...arr]; a[i] = { ...a[i], [k]: v }; return a; });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/content/kv/home_sections", { value: { ...heroSubHome, hero_rotation: rotation, hero_rotation_seconds: seconds, cases_per_row: casesPerRow, classic_label: classicLabel, clients_page_size: clientsPageSize, clients_rotate_sec: clientsRotateSec, clients_logo_scale: clientsLogoScale } });
      toast.success("Configurações salvas.");
    } catch { toast.error("Erro ao salvar."); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl">Hero & Cases · Layout</h1>
          <p className="text-sm text-neutral-500 mt-1 max-w-xl">Configure a alternância de títulos no hero da Home e quantos cases exibir por linha na página /cases.</p>
        </div>
        <button onClick={save} disabled={busy} data-testid="hero-save" className="bg-black text-white text-sm px-5 py-2.5 rounded-full hover:bg-accent-orange transition-colors disabled:opacity-50">
          <Save className="w-3 h-3 inline mr-1" /> {busy ? "Salvando…" : "Salvar"}
        </button>
      </div>

      {/* MARCAS DE CLIENTES (home) */}
      <section className="border border-black/10 rounded-md p-5 mb-6" data-testid="clients-logos-config">
        <div className="text-xs uppercase tracking-widest text-accent-orange font-bold">Home · Marcas de clientes</div>
        <div className="font-display text-2xl mb-1">Vitrine de logos</div>
        <p className="text-sm text-neutral-500 mb-4 max-w-2xl">Controla a seção “Marcas que fizeram história com a gente”. Os logos vêm de <strong>Clientes</strong> no menu Conteúdo.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-neutral-500 flex justify-between">
              Tamanho do logo <span className="font-mono text-black">{clientsLogoScale}%</span>
            </label>
            <input type="range" min={30} max={95} step={2} value={clientsLogoScale}
              onChange={(e) => setClientsLogoScale(Number(e.target.value))}
              data-testid="clients-logo-scale" className="w-full" />
            <p className="text-[10px] text-neutral-400 mt-1">% da altura da célula. 72% é o padrão; suba se as marcas estiverem parecendo pequenas.</p>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-neutral-500">Logos por página</label>
            <input type="number" min={6} max={60} value={clientsPageSize}
              onChange={(e) => setClientsPageSize(Number(e.target.value))}
              data-testid="clients-page-size"
              className="w-full border border-black/15 px-2 py-1.5 rounded text-sm" />
            <p className="text-[10px] text-neutral-400 mt-1">Grid de 6 colunas no desktop — 18 = 3 linhas.</p>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-neutral-500">Troca de página (segundos)</label>
            <input type="number" min={3} max={60} value={clientsRotateSec}
              onChange={(e) => setClientsRotateSec(Number(e.target.value))}
              data-testid="clients-rotate-sec"
              className="w-full border border-black/15 px-2 py-1.5 rounded text-sm" />
          </div>
        </div>
      </section>

      {/* HERO ROTATION */}
      <section className="border border-black/10 rounded-md p-5 mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-accent-orange font-bold">Home Hero</div>
            <div className="font-display text-2xl">Títulos alternantes ({rotation.length})</div>
          </div>
          <button onClick={add} data-testid="hero-add" className="text-xs bg-black text-white px-3 py-2 rounded-full font-semibold hover:bg-accent-yellow hover:text-black transition-colors">
            <Plus className="w-3 h-3 inline mr-1" /> Adicionar título
          </button>
        </div>

        <div className="mb-4 flex items-center gap-2 text-sm">
          <Clock className="w-4 h-4 text-neutral-500" />
          <span className="text-neutral-500">Tempo entre trocas:</span>
          <input type="number" min={2} max={60} value={seconds} onChange={(e) => setSeconds(Number(e.target.value))}
            data-testid="hero-seconds"
            className="w-20 border border-black/15 px-2 py-1 rounded text-sm" />
          <span className="text-neutral-500">segundos</span>
        </div>

        <div className="space-y-3">
          {rotation.map((r, i) => (
            <div key={i} className="border border-black/10 rounded-md p-4" data-testid={`hero-rotation-${i}`}>
              <div className="flex justify-between items-center mb-2">
                <div className="text-xs uppercase tracking-widest text-neutral-500">Título {i + 1}</div>
                <div className="flex items-center gap-1">
                  <button onClick={() => move(i, -1)} disabled={i === 0}
                    data-testid={`hero-move-up-${i}`}
                    className="text-xs text-neutral-500 hover:text-black disabled:opacity-30 disabled:cursor-not-allowed p-1" title="Mover para cima">
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button onClick={() => move(i, 1)} disabled={i === rotation.length - 1}
                    data-testid={`hero-move-down-${i}`}
                    className="text-xs text-neutral-500 hover:text-black disabled:opacity-30 disabled:cursor-not-allowed p-1" title="Mover para baixo">
                    <ArrowDown className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(i)} data-testid={`hero-remove-${i}`} className="text-xs text-red-500 hover:text-red-700 p-1" title="Remover">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <label className="text-[11px] text-neutral-500">Título (HTML permitido: em, span, strong, i, b, br)</label>
              <textarea value={r.title_html} onChange={(e) => upd(i, "title_html", e.target.value)}
                data-testid={`hero-title-${i}`}
                rows={3}
                placeholder='ex: Seu empreendimento com <span class="text-accent-orange">narrativa potente</span>...'
                className="w-full border border-black/15 px-3 py-2 rounded text-sm mt-1 mb-2 font-mono" />
              <label className="text-[11px] text-neutral-500">Subtítulo</label>
              <textarea value={r.subtitle} onChange={(e) => upd(i, "subtitle", e.target.value)}
                data-testid={`hero-subtitle-${i}`}
                rows={2}
                className="w-full border border-black/15 px-3 py-2 rounded text-sm mt-1" />
            </div>
          ))}
          {rotation.length === 0 && (
            <div className="text-center border border-dashed border-black/15 rounded p-6 text-sm text-neutral-500">
              Sem títulos alternantes. Se vazio, o hero mostra o título padrão. Adicione pelo menos 2 para ativar a rotação.
            </div>
          )}
        </div>
      </section>

      {/* CASES PER ROW */}
      <section className="border border-black/10 rounded-md p-5">
        <div className="text-xs uppercase tracking-widest text-accent-orange font-bold mb-2">Página /cases</div>
        <div className="font-display text-2xl mb-4">Layout de Cases</div>
        <div className="flex items-center gap-3 mb-6">
          <LayoutGrid className="w-4 h-4 text-neutral-500" />
          <span className="text-sm text-neutral-500">Cases por linha:</span>
          {[2, 3, 4].map((n) => (
            <button key={n} onClick={() => setCasesPerRow(n)}
              data-testid={`cases-per-row-${n}`}
              className={`px-4 py-2 text-sm rounded-full border transition ${casesPerRow === n ? "bg-black text-white border-black" : "border-black/15 hover:bg-black/5"}`}>
              {n} colunas
            </button>
          ))}
        </div>

        <div className="border-t border-black/10 pt-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1 flex items-center gap-1">
            <span className="text-accent-yellow">👑</span> Rótulo da coroa "Clássicos Places"
          </div>
          <input
            value={classicLabel}
            onChange={(e) => setClassicLabel(e.target.value)}
            placeholder="Clássicos Places"
            data-testid="classic-label"
            className="w-full max-w-md border border-black/15 px-3 py-2 rounded text-sm"
          />
          <div className="text-[11px] text-neutral-500 mt-1">Texto exibido no tooltip ao passar o mouse sobre a coroa nos thumbs de cases marcados como <b>Clássicos Places</b>.</div>
        </div>
      </section>
    </div>
  );
}
